/**
 * Pure helper: builds the ordered Apply Plan for the Bulk Similarity
 * Triage page. Consumed by MergePreviewModal to render the "dry-run"
 * preview in the SAME order executeApply will run.
 *
 * Order (must match executeApply in BulkSimilarityPage.jsx):
 *   1. merge-staging   — staging→staging (topologically ordered)
 *   2. merge-approved  — staging→approved
 *   3. skip            — soft-delete
 *   4. keep            — promote to approved DB
 *
 * Also detects cross-reference WARNINGS:
 *   • A row used as a merge target that also has action='keep'
 *     (safe under new order, but worth surfacing so admin knows the
 *     target will be promoted WITH the absorbed history)
 *   • A row used as a merge target that also has action='skip'
 *     (data goes to trash — recoverable, but rarely intentional)
 *
 * Kept in its own module for jest testability without dragging in the
 * full page component graph.
 */

/**
 * @param {Object} params
 * @param {string[]} params.skipIds
 * @param {string[]} params.keepIds
 * @param {Object} params.analysis  — output of analyzeMergeOps(rowState)
 * @param {Object} params.rowState  — the raw rowState (used for cross-ref detection)
 * @param {Array}  params.allRows   — [{staging_question_id, row_number, question_text}, ...]
 * @returns {Object} plan
 */
export function buildApplyPlan({ skipIds, keepIds, analysis, rowState, allRows }) {
  // Two sources of truth for row labels:
  //  1. allRows[] — the primary triage rows in the current batch.
  //  2. allRows[*].staging_similar[*] — cross-batch/same-batch match cards
  //     that CAN be selected as merge targets. Without this fallback, a
  //     target in a different batch renders as a raw UUID in the preview.
  const byId = new Map();
  for (const r of allRows || []) {
    byId.set(r.staging_question_id, r);
    for (const m of r.staging_similar || []) {
      // Match cards don't have row_number in the same sense (they carry
      // the IMMUTABLE CSV row_number rather than a page position). Keep
      // both so the modal RowChip can render "Row 225 (keywords.csv)".
      if (!byId.has(m.id)) {
        byId.set(m.id, {
          staging_question_id: m.id,
          row_number: m.row_number ?? null,
          question_text: m.question_text,
          source_filename: m.source_filename || null,
          _isCrossBatchMatch: true,
        });
      }
    }
  }
  const labelFor = (sid) => {
    const r = byId.get(sid);
    if (!r) return { rowNumber: null, text: sid, id: sid };
    return {
      rowNumber: r.row_number ?? null,
      text: (r.question_text || '').trim(),
      id: sid,
      isCrossBatchMatch: !!r._isCrossBatchMatch,
      sourceFilename: r.source_filename || null,
    };
  };

  // Step 1: staging→staging merges (already topologically ordered by analyzer)
  const mergeStagingOps = (analysis.orderedOps || []).map((o) => ({
    kind: 'merge-staging',
    source: labelFor(o.source_staging_id),
    target: labelFor(o.target_staging_id),
  }));

  // Step 2: staging→approved merges. Approved targets aren't in allRows —
  // display the truncated approved-question ID so admins can cross-check.
  const mergeApprovedOps = (analysis.approvedOps || []).map((o) => ({
    kind: 'merge-approved',
    source: labelFor(o.source),
    target: { rowNumber: null, text: `Approved Q ${o.target}`, id: o.target, isApproved: true },
  }));

  // Step 3: skip
  const skipOps = (skipIds || []).map((sid) => ({
    kind: 'skip',
    row: labelFor(sid),
  }));

  // Step 4: keep
  const keepOps = (keepIds || []).map((sid) => ({
    kind: 'keep',
    row: labelFor(sid),
  }));

  // --- Cross-reference warnings ---
  // Build a map of every row that's being USED as a merge target and
  // record which sources will land there. Then check if that target
  // ALSO has its own action (keep/skip) — if so, surface the interaction.
  const inboundByTarget = new Map(); // targetId -> [sourceId, ...]
  for (const [sid, s] of Object.entries(rowState || {})) {
    if (s.action !== 'merge' || !s.mergeTargetId) continue;
    if (s.mergeTargetSource !== 'staging') continue; // approved targets aren't in staging
    if (!inboundByTarget.has(s.mergeTargetId)) inboundByTarget.set(s.mergeTargetId, []);
    inboundByTarget.get(s.mergeTargetId).push(sid);
  }

  const warnings = [];
  for (const [targetId, sources] of inboundByTarget) {
    const targetState = rowState[targetId];
    if (!targetState) continue;
    if (targetState.action === 'keep') {
      warnings.push({
        kind: 'target-also-keep',
        target: labelFor(targetId),
        sources: sources.map(labelFor),
      });
    } else if (targetState.action === 'skip') {
      warnings.push({
        kind: 'target-also-skip',
        target: labelFor(targetId),
        sources: sources.map(labelFor),
      });
    }
  }

  const totalOps =
    mergeStagingOps.length + mergeApprovedOps.length + skipOps.length + keepOps.length;

  return {
    steps: [
      { kind: 'merge-staging', label: 'Merge (staging → staging)', ops: mergeStagingOps, runsFirst: true },
      { kind: 'merge-approved', label: 'Merge (staging → approved)', ops: mergeApprovedOps },
      { kind: 'skip', label: 'Skip (soft-delete to Trash)', ops: skipOps },
      { kind: 'keep', label: 'Keep (promote to approved DB)', ops: keepOps, runsLast: true },
    ],
    warnings,
    cycles: analysis.cycles || [],
    chains: analysis.chains || [],
    hasErrors: !!analysis.hasErrors,
    totalOps,
    isEmpty: totalOps === 0 && !analysis.hasErrors,
  };
}
